import xlsx, { ISettings, IJsonSheet, IColumn, IContent } from "json-as-xlsx";
import moment from "moment";
import {
  RelatorioComposicaoContasPdfResponse,
  ResumoCategoriaItem,
  RelatorioComposicaoContasItem,
  ComposicoesRelatorioComposicaoContas,
} from "core/features/relatorios/conta-com-composicoes/typings";
import { formatMoney } from "infra/helpers/number";
import { defaultContentMapperXlsx } from "infra/packages/json-as-xlsx/index";
import { sanitizeSheetName } from "infra/helpers/sanitizeSheetName";
import { OperacaoEnum } from "resources/enums/conta";
import { RelatorioContasComComposicaoFields } from "core/features/relatorios/typings";
import { mapFormaPagamentoToLabel } from "infra/helpers/mapFormaPagamentoToLabel";
import { FormaPagamento } from "core/resources/enums/tenant";
import { formatBlocoQuadra } from "infra/helpers/formatBlocoQuadra";

type TotaisSheetItem = Pick<
  RelatorioComposicaoContasPdfResponse,
  "totalCobrancas" | "totalUnidades" | "valorTotal"
>;

type TItemCorrido = {
  nome?: string;
  categoria?: string;
  complemento?: string;
  dataCompetencia?: string;
  dataVencimento?: string;
  dataPagamento?: string;
  dataMovimentacao?: string;
  dataCriacao?: string;
  centroDeCusto?: string;
  valor?: number;
  descricao?: string;
  nossoNumero?: string | null;
  formaPagamentoId?: FormaPagamento | null;
  tenantConta?: {
    id: string;
    nome: string;
  } | null;
  pessoaOperadorAcordo?: {
    pessoaId: string;
    nome: string
  } | null;
};

export function exportExcelRelatorioComposicaoContas({
  data,
  operacao,
  fields,
}: {
  data: RelatorioComposicaoContasPdfResponse;
  operacao: OperacaoEnum;
  fields?: RelatorioContasComComposicaoFields[];
}) {
  const settings: ISettings = {
    fileName: `Relatório de ${operacao === OperacaoEnum.Despesa ? "Despesas" : "Receitas"
      } com Composição - ${moment().format("DD_MM_YYYY")}`,
    extraLength: 3,
    writeOptions: {},
  };

  const columnsUnidades = getColumnsUnidades(operacao);
  const columnsTotais = getColumnsTotais(operacao);

  const columns = getColumns(fields);
  const columnsRelatorioCorrido = getColumnsItemsCorridos(fields);

  const sheets: IJsonSheet[] = [];

  const itemsCorridos = data.items.reduce((acc, next) => {
    acc.push({
      nome: `${next.unidade
        ? formatBlocoQuadra(next.unidade, next.blocoQuadra, "") + " - "
        : ""
        }${next.nome}`,
      pessoaOperadorAcordo: next?.pessoaOperadorAcordo
        ? {
          pessoaId: next?.pessoaOperadorAcordo?.pessoaId,
          nome: next?.pessoaOperadorAcordo?.nome,
        }
        : null,
    });

    next.composicoes.forEach((composicao) =>
      acc.push({
        categoria: `${composicao.categoriaFinanceira?.breadcrumb} ${composicao?.categoriaFinanceira?.nome}`,
        centroDeCusto: composicao?.centroDeCusto?.nome,
        dataCompetencia: composicao?.dataCompetencia,
        dataMovimentacao: composicao?.dataMovimentacao ?? "",
        dataCriacao: composicao?.dataCriacao ?? "",
        complemento: composicao?.complemento ?? "",
        dataPagamento: composicao?.dataPagamento ?? "",
        dataVencimento: composicao?.dataVencimento ?? "",
        descricao: composicao?.descricao ?? "",
        valor: composicao?.valor,
        nossoNumero: composicao?.nossoNumero ?? null,
        formaPagamentoId: composicao?.formaPagamentoId,
        tenantConta: composicao?.tenantConta
          ? {
            id: composicao?.tenantConta?.id,
            nome: composicao?.tenantConta?.nome,
          }
          : null,
      }),
    );

    acc.push({ nome: "", valor: next.valorTotal });

    // Isso serve para pular uma linha
    acc.push({ nome: "" });
    return acc;
  }, [] as TItemCorrido[]);

  sheets.push({
    sheet: `Relatório`,
    columns: columnsRelatorioCorrido,
    content: itemsCorridos.map(defaultContentMapperXlsx),
  });

  sheets.push({
    sheet: `${operacao === OperacaoEnum.Despesa ? "Despesas" : "Receitas"
      } (${formatMoney(data.valorTotal)})`.slice(0, 31),
    columns: columnsUnidades,
    content: data.items.map(defaultContentMapperXlsx),
  });

  sheets.push({
    sheet: `Totais`,
    columns: columnsTotais,
    content: [
      {
        totalCobrancas: data.totalCobrancas,
        totalUnidades: data.totalUnidades,
        valorTotal: data.valorTotal,
      },
    ],
  });

  if (fields?.includes("resumoContaCategoria")) {
    const columnsResumo = getColumnsResumo();

    sheets.push({
      sheet: "Resumo por categoria",
      columns: columnsResumo,
      content: data.resumoPorCategoria.map(defaultContentMapperXlsx),
    });
  }

  data.items.forEach((item) => {
    const sheetName = `${item.unidade
      ? formatBlocoQuadra(item.unidade, item.blocoQuadra, "") + " - "
      : ""
      }${item.nome}`;
    sheets.push({
      sheet: sanitizeSheetName(sheetName.slice(0, 31)),
      columns: columns,
      content: item.composicoes.map(defaultContentMapperXlsx),
    });
  });

  xlsx(sheets, settings);
}

function getColumnsItemsCorridos(
  fields?: RelatorioContasComComposicaoFields[],
) {
  const columns: IColumn[] = [];

  columns.push({
    label: "Unidade - Nome",
    value: (row: IContent) => (row as unknown as TItemCorrido).nome ?? "",
  });


  if (fields?.includes("pessoaOperadorAcordo")) {
    columns.push({
      label: "Usuário operador do acordo",
      value: (row: IContent) =>
        (row as unknown as TItemCorrido).pessoaOperadorAcordo?.nome ?? "",
    });
  }

  if (fields?.includes("categoria")) {
    columns.push({
      label: "Categoria",
      value: (row: IContent) =>
        (row as unknown as TItemCorrido).categoria ?? "",
    });
  }

  if (fields?.includes("complemento")) {
    columns.push({
      label: "Complemento",
      value: (row: IContent) =>
        (row as unknown as TItemCorrido).complemento ?? "",
    });
  }

  if (fields?.includes("competencia")) {
    columns.push({
      label: "Competência",
      format: "mm/yyyy",
      value: (row: IContent) => {
        const date = (row as unknown as TItemCorrido).dataCompetencia;

        return date ? moment(date, "YYYY-MM").format("MM/YYYY") : "";
      },
    });
  }

  if (fields?.includes("dataVencimento")) {
    columns.push({
      label: "Vencimento",
      format: "dd/mm/yyyy",
      value: (row: IContent) => {
        const date = (row as unknown as TItemCorrido).dataVencimento;

        return date
          ? moment((row as unknown as TItemCorrido).dataVencimento).format(
            "DD/MM/YYYY",
          )
          : "";
      },
    });
  }

  if (fields?.includes("dataPagamento")) {
    columns.push({
      label: "Liquidação",
      format: "dd/mm/yyyy",
      value: (row: IContent) => {
        const date = (row as unknown as TItemCorrido).dataPagamento;

        return date ? moment(date).format("DD/MM/YYYY") : "";
      },
    });
  }

  if (fields?.includes("dataMovimentacao")) {
    columns.push({
      label: "Crédito",
      format: "dd/mm/yyyy",
      value: (row: IContent) => {
        const date = (row as unknown as TItemCorrido).dataMovimentacao;

        return date ? moment(date).format("DD/MM/YYYY") : "";
      },
    });
  }


  if (fields?.includes("dataCriacao")) {
    columns.push({
      label: "Data de criação",
      format: "dd/mm/yyyy",
      value: (row: IContent) => {
        
        const date = (row as unknown as TItemCorrido).dataCriacao;

        return date ? moment(date).format("DD/MM/YYYY") : "";
      },
    });
  }

  if (fields?.includes("centroDeCusto")) {
    columns.push({
      label: "Centro de custo",
      value: (row: IContent) =>
        (row as unknown as TItemCorrido).centroDeCusto ?? "",
    });
  }

  if (fields?.includes("valor")) {
    columns.push({
      label: "Total",
      format: "#,###0.00",
      value: (row: IContent) => (row as unknown as TItemCorrido).valor || "",
    });
  }

  if (fields?.includes("formaPagamento")) {
    columns.push({
      label: "Forma de pagamento",
      value: (row: IContent) =>
        mapFormaPagamentoToLabel(
          (row as unknown as TItemCorrido).formaPagamentoId ?? undefined,
          "",
        ) || "",
    });
  }

  if (fields?.includes("tenantConta")) {
    columns.push({
      label: "Conta bancária",
      value: (row: IContent) =>
        (row as unknown as TItemCorrido).tenantConta?.nome ?? "",
    });
  }

  if (fields?.includes("descricao")) {
    columns.push({
      label: "Descrição",
      value: (row: IContent) => (row as unknown as TItemCorrido).descricao!,
    });
  }

  if (fields?.includes("nossoNumero")) {
    columns.push({
      label: "Nosso Número",
      value: (row: IContent) =>
        (row as unknown as TItemCorrido).nossoNumero ?? "",
    });
  }

  return columns;
}

function getColumnsUnidades(operacao: OperacaoEnum) {
  const columns: IColumn[] = [];

  columns.push({
    label: "Unidade",
    value: (row: IContent) =>
      formatBlocoQuadra(
        (row as unknown as RelatorioComposicaoContasItem).unidade,
        (row as unknown as RelatorioComposicaoContasItem).blocoQuadra,
        "",
      ),
  });

  columns.push({
    label: operacao === OperacaoEnum.Receita ? "Nome" : "Favorecido",
    value: (row: IContent) =>
      (row as unknown as RelatorioComposicaoContasItem).nome,
  });

  columns.push({
    label: "Valor",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as RelatorioComposicaoContasItem).valorTotal,
  });

  return columns;
}

function getColumns(fields?: RelatorioContasComComposicaoFields[]) {
  const columns: IColumn[] = [];

  if (fields?.includes("categoria")) {
    columns.push({
      label: "Categoria",
      value: (row: IContent) =>
        `${(row as unknown as ComposicoesRelatorioComposicaoContas)
          ?.categoriaFinanceira?.breadcrumb!} ${(
            row as unknown as ComposicoesRelatorioComposicaoContas
          )?.categoriaFinanceira?.nome!}`,
    });
  }

  if (fields?.includes("complemento")) {
    columns.push({
      label: "Complemento",
      value: (row: IContent) =>
        (row as unknown as ComposicoesRelatorioComposicaoContas).complemento ??
        "",
    });
  }

  if (fields?.includes("competencia")) {
    columns.push({
      label: "Competência",
      format: "mm/yyyy",
      value: (row: IContent) =>
        moment(
          (row as unknown as ComposicoesRelatorioComposicaoContas)
            .dataCompetencia,
          "YYYY-MM",
        ).format("MM/YYYY"),
    });
  }

  if (fields?.includes("dataVencimento")) {
    columns.push({
      label: "Vencimento",
      format: "dd/mm/yyyy",
      value: (row: IContent) =>
        moment(
          (row as unknown as ComposicoesRelatorioComposicaoContas)
            .dataVencimento,
        ).format("DD/MM/YYYY"),
    });
  }

  if (fields?.includes("dataPagamento")) {
    columns.push({
      label: "Liquidação",
      format: "dd/mm/yyyy",
      value: (row: IContent) => {
        const date = (row as unknown as ComposicoesRelatorioComposicaoContas)
          .dataPagamento;

        return date ? moment(date).format("DD/MM/YYYY") : "";
      },
    });
  }

  if (fields?.includes("dataMovimentacao")) {
    columns.push({
      label: "Crédito",
      format: "dd/mm/yyyy",
      value: (row: IContent) => {
        const date = (row as unknown as ComposicoesRelatorioComposicaoContas)
          .dataMovimentacao;

        return date ? moment(date).format("DD/MM/YYYY") : "";
      },
    });
  }

  if (fields?.includes("dataCriacao")) {
    columns.push({
      label: "Data de criação",
      format: "dd/mm/yyyy",
      value: (row: IContent) => {
        const date = (row as unknown as ComposicoesRelatorioComposicaoContas)
          .dataCriacao;

        return date ? moment(date).format("DD/MM/YYYY") : "";
      },
    });
  }

  if (fields?.includes("centroDeCusto")) {
    columns.push({
      label: "Centro de custo",
      value: (row: IContent) =>
        (row as unknown as ComposicoesRelatorioComposicaoContas).centroDeCusto
          ?.nome ?? "",
    });
  }

  if (fields?.includes("valor")) {
    columns.push({
      label: "Total",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as ComposicoesRelatorioComposicaoContas).valor ?? "",
    });
  }

  if (fields?.includes("formaPagamento")) {
    columns.push({
      label: "Forma de pagamento",
      value: (row: IContent) =>
        mapFormaPagamentoToLabel(
          (row as unknown as ComposicoesRelatorioComposicaoContas)
            .formaPagamentoId ?? undefined,
          "",
        ) || "",
    });
  }

  if (fields?.includes("tenantConta")) {
    columns.push({
      label: "Conta bancária",
      value: (row: IContent) =>
        (row as unknown as ComposicoesRelatorioComposicaoContas).tenantConta
          ?.nome ?? "",
    });
  }

  if (fields?.includes("nossoNumero")) {
    columns.push({
      label: "Nosso Número",
      value: (row: IContent) =>
        (row as unknown as ComposicoesRelatorioComposicaoContas).nossoNumero ??
        "",
    });
  }

  if (fields?.includes("descricao")) {
    columns.push({
      label: "Descrição",
      value: (row: IContent) =>
        (row as unknown as ComposicoesRelatorioComposicaoContas).descricao!,
    });
  }

  return columns;
}

function getColumnsTotais(operacao: OperacaoEnum) {
  const columns: IColumn[] = [];

  if (operacao === OperacaoEnum.Receita) {
    columns.push({
      label: "Número de unidades",
      format: "#,###0",
      value: (row: IContent) =>
        (row as unknown as TotaisSheetItem).totalUnidades,
    });
  }

  columns.push({
    label: "Valor total",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as TotaisSheetItem).valorTotal,
  });

  return columns;
}

function getColumnsResumo() {
  const columns: IColumn[] = [];

  columns.push({
    label: "Categoria",
    value: (row: IContent) => (row as unknown as ResumoCategoriaItem).nome,
  });

  columns.push({
    label: "Total",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as ResumoCategoriaItem).valor!,
  });

  return columns;
}
